import { Button } from "@mui/material";
import dayjs from "dayjs";
import { useFormik } from "formik";
import { useState } from "react";
import * as Yup from "yup";
import CustomDatePicker from "../../../../../components/CustomDatePicker";
import CustomTextArea from "../../../../../components/CustomTextArea";
import Flex from "../../../../../components/Flex";
import SideSheetDrawer from "../../../../../components/SidesheetDrawer";
import { DisclosureHost } from "../../../../../hooks/use-disclosure";
import { useSnackbar } from "../../../../../hooks/useSnackbar";
import {
  DEF_PAGINATION,
  DGT_Pagination,
  getXtenantId,
  showToastOnApiCall,
} from "../../../../../utils/utils";
import { useBatchReassign } from "../../../../../queries/collaboration/tasks/use-batch-reassign";
import { useQueryClient } from "@tanstack/react-query";
import AsyncSelectScroll from "@/components/AsyncSelectScroll";

import { useProvidersForReassign } from "../../../../../queries/provider/setting/use-providers-for-reassign";
import { useDebounce } from "../../../../../hooks/use-debounce";
interface BatchDrawerProps {
  disclosureHost: DisclosureHost;
  setSelectedTaskModel: (model: string[]) => void;
  title?: string;
}

interface FormValues {
  assignee: { value: string; label: string } | null;
  date: dayjs.Dayjs | null;
  note: string;
}

const validationSchema = Yup.object().shape({
  assignee: Yup.object().required("Please select a user to reassign the task."),
  date: Yup.date().required("Due Date is required"),
  note: Yup.string(),
});

const BatchReassignTaskDrawer = ({
  disclosureHost,
  setSelectedTaskModel,
  title = "Reassign Task",
}: BatchDrawerProps) => {
  const showSnackbar = useSnackbar();

  const { mutateAsync: batchReassign } = useBatchReassign();
  const queryClient = useQueryClient();
  const [primaryProviderSearch, setPrimaryProviderSearch] =
    useDebounce<string>("");
  const [providerPagination, setProviderPagination] =
    useState<DGT_Pagination>(DEF_PAGINATION);
  const { data: providersList, isFetching: isProvidersFetching } =
    useProvidersForReassign({
      pagination: providerPagination,
      searchKey: primaryProviderSearch,
    });

  const handleSubmit = async (values: FormValues) => {
    showToastOnApiCall({
      apiCall: batchReassign({
        bulkReassigned: {
          taskId: disclosureHost.data,
          assignTo: { [values.assignee?.value ?? ""]: "" },
          dueDate: values.date?.toISOString() || "",
          description: values.note || "",
        },
        xTENANTID: getXtenantId(),
      }),
      showSnackbar,
      onSave: () => {
        setSelectedTaskModel([]);
        queryClient.invalidateQueries({
          queryKey: ["collaboration-task-list"],
        });
        handleClose();
      },
    });
  };

  const handleClose = () => {
    formik.resetForm();
    disclosureHost.close();
  };

  const formik = useFormik<FormValues>({
    initialValues: {
      assignee: null,
      date: dayjs(new Date()),
      note: "",
    },
    validationSchema: validationSchema,
    onSubmit: handleSubmit,
  });

  return (
    <SideSheetDrawer
      open={disclosureHost.opened}
      onClose={handleClose}
      title={title}
      w="44vw"
      footerActions={
        <>
          <Button
            type="button"
            variant="outlined"
            disabled={formik.isSubmitting}
            onClick={handleClose}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            variant="contained"
            disabled={formik.isSubmitting}
            onClick={() => formik.handleSubmit()}
          >
            Assign
          </Button>
        </>
      }
    >
      <form onSubmit={formik.handleSubmit}>
        <Flex flexDirection={"column"} gap={3} width={"100%"}>
          <AsyncSelectScroll
            required
            label="Assignee"
            options={providersList?.data?.content?.map((it) => ({
              value: it?.uuid,
              label: it?.firstName + " " + it?.lastName,
            }))}
            placeholder="Search Assignee"
            onSearch={(value) => {
              setPrimaryProviderSearch(value);
              setProviderPagination((p) => ({ ...p, page: 0 }));
            }}
            value={formik.values.assignee}
            onChange={(value) => {
              formik.setFieldValue("assignee", value);
            }}
            error={(formik.touched?.assignee && formik.errors?.assignee) as any}
            pagination={providerPagination}
            setPagination={setProviderPagination}
            totalElements={providersList?.data?.page?.totalElements}
            loading={isProvidersFetching}
          />

          <CustomDatePicker
            minDate={dayjs(new Date())}
            label="Due Date"
            value={formik.values.date}
            onChange={(value) => formik.setFieldValue("date", value)}
            required
          />
          <CustomTextArea
            label="Note"
            value={formik.values.note}
            onChange={(e) => formik.setFieldValue("note", e.target.value)}
            minRows={5}
          />
        </Flex>
      </form>
    </SideSheetDrawer>
  );
};

export default BatchReassignTaskDrawer;
